import React, { useCallback, useMemo } from 'react'
import { PanelMessageOut } from './types'
import { checkPollinationPanel } from './utilities'
import { Job, Run, RunStatusEnum } from '@pollination-solutions/pollination-sdk'
import { ProjectJobInfo } from './useCreateStudy'

export enum TargetPlaform {
  all = 'all',
  rhino = 'rhino',
  revit = 'revit',
  grasshopper = 'grasshopper',
  sketchup = 'sketchup'
}

enum SimulationFileType 
{
  status,
  job,
  logs
}

async function blobToBase64(blob) {
  return new Promise((resolve, _) => {
    const reader = new FileReader()
    reader.onloadend = () =>  {
      if (typeof reader.result !== 'string') { 
        return
      }
      resolve(reader.result.split(',')[1])
    }

    reader.readAsDataURL(blob)
  })
}

export const usePollinationPanel = () => {

  const panel: { 
    GetArtifactAsBase64: (key: string, accept: string ) => PanelMessageOut,
    FromBase64ToFile: (key: string, base64: string, subFolder: string, filename: string) => PanelMessageOut,
    FromFileToBase64: (filePath: string) => PanelMessageOut,
    GetFilePaths: (root: string) => PanelMessageOut,
    FileExplorer: (root: string) => PanelMessageOut,
    GetSimulationFolder: () => string,
    GetCpuCount: () => number,

    // Local run methods
    FetchSimulationFile: (path: string, subfolder: string, type: string) => PanelMessageOut,
    GetPaginatedJob: (projectSlug: string, 
      pageSize: number, 
      curPage: number, 
      status: RunStatusEnum | undefined, 
      platform: TargetPlaform) => PanelMessageOut
    GetJob: (projectSlug: string) => PanelMessageOut,
    DeleteJob: (projectSlug: string, path: string, subfolder: string) => PanelMessageOut
  } = useMemo(() =>{
    return checkPollinationPanel() && window.parent.chrome.webview.hostObjects.sync.study
  }, [])

  /**
   * Get local simulation path from po-panel
   */
  const simulationPath = useMemo(() => {
    if (!panel) return

    return panel.GetSimulationFolder()
  }, [panel])

  /**
   * Get CPU count from po-panel
   */
  const cpuCount = useMemo(() => {
    if (!panel) return

    return panel.GetCpuCount()
  }, [panel])

  /**
   * From base64 to file
   */
  const saveFileBase64 = useCallback(async (
    key: string,
    file: File | Blob,
    subFolder: string, 
    fileName: string
  ) => {
    if (!panel) return

    const base64 = await blobToBase64(file)

    if (typeof base64 !== 'string') {
      console.error(base64)
      return null
    }
    
    return panel.FromBase64ToFile(key, base64, subFolder, fileName)

  }, [panel])
  
  /**
   * Select a file and generate base64 representation
   */
  const getFileBase64 = useCallback((
    key = performance.now().toString(),
    accept = 'HBJSON|*.hbjson'
  ) => {
    if (!panel) return

    return panel.GetArtifactAsBase64(key, accept)
  }, [panel])

  /**
   * Get file paths from dir
   */
  const getFilesFromDir = useCallback((root) => {
    if (!panel) return

    return panel.GetFilePaths(root)
  }, [panel])

  /**
   * From file path to base64
   */
  const fromFileToBase64 = useCallback((
    filePath: string
  ) => {
    if (!panel) return

    return panel.FromFileToBase64(filePath)
  }, [panel])

  /**
   * Run file explorer
   */
  const fileExplorer = useCallback((
    root: string
  ) => {
    if (!panel) return

    return panel.FileExplorer(root)
  }, [panel])

  /**
   * Fetch local run
   */
  const fetchLocalRun = useCallback((
    path = '',
    subfolder = undefined
  ) => {
    if (!panel) return

    try {
      const message = panel.FetchSimulationFile(path, subfolder, SimulationFileType[SimulationFileType.status])
      if (message.data === '') {
        console.log('[LOCAL RUN]: Status file not found')
      }

      const run: Run = JSON.parse(message.data)
      return run
    } catch (error) {
      throw error
    }
  }, [panel])

  /**
   * Fetch local job
   */
  const fetchLocalJob = useCallback((
    path = '',
    subfolder = undefined
  ) => {
    if (!panel) return

    try {
      const message = panel.FetchSimulationFile(path, subfolder, SimulationFileType[SimulationFileType.job])
      if (message.data === '') {
        console.log('[LOCAL RUN]: Job file not found')
      }

      const localJob = JSON.parse(message.data)
      const job: Job = localJob?.LocalJob?.Job
      job['author'] = localJob?.LocalJob?.JobAuthor
      return job
    } catch (error) {
      // Do nothing. It is written at the end
      return undefined
    }
  }, [panel])

  /**
   * Fetch local log
   */
  const fetchLocalLog = useCallback((
    path = '',
    subfolder = undefined
  ) => {
    if (!panel) return

    try {
      const message = panel.FetchSimulationFile(path, subfolder, SimulationFileType[SimulationFileType.logs])
      if (message.data === '') {
        console.log('[LOCAL RUN]: Log file not found')
      }

      return message.data
    } catch (error) {
      // Do nothing. It is written at the end
      return undefined
    }
  }, [panel])

  /**
   * Get array of ProjectJobInfo from DB
   * @param projectSlug Project slug <USER/PROJECT_NAME>
   * @param pageSize Page size
   * @param curPage Current page
   * @returns Array of ProjectJobInfo
   */
  const getPaginatedJob = (projectSlug: string, 
    pageSize: number, 
    curPage: number,
    status: RunStatusEnum = undefined,
    platform: TargetPlaform = TargetPlaform.all) => {
    if (!panel) return

    try {
      const message = panel.GetPaginatedJob(projectSlug, pageSize, 
        curPage, status, platform)
      const arr: ProjectJobInfo[] = JSON.parse(message.data)
      return arr
    } catch (error) {
      // Do nothing. It is written at the end
      return undefined
    }
  }

  /**
   * Get array of ProjectJobInfo from DB
   * @param projectSlug Project slug
   * @returns Array of ProjectJobInfo
   */
  const getJob = (projectSlug: string) => {
    if (!panel) return

    try {
      const message = panel.GetJob(projectSlug)
      const arr: ProjectJobInfo[] = JSON.parse(message.data)
      return arr
    } catch (error) {
      // Do nothing. It is written at the end
      return undefined
    }
  }

  /**
   * Count number of simulation by platform
   * @param projectSlug Project slug
   * @returns Object with platform count
   */
  const getPlatforms = (projectSlug: string) => {
    if (!panel) return

    let count = {}
    try {
      const message = panel.GetJob(projectSlug)
      const arr: ProjectJobInfo[] = JSON.parse(message.data)

      arr.forEach((i) => { count[i.platform] = (count[i.platform] || 0) + 1 })
      
      return count
    } catch (error) {
      // Do nothing. It is written at the end
      return undefined
    }
  }

  /**
   * Delete job with the same input/ouput folder from DB
   * @param projectSlug Project slug
   * @param path Location where files are
   * @param subfolder Subfolder
   * @returns Message with number of deleted records
   */
    const deleteJob = (projectSlug: string, path: string, subfolder: string) => {
      if (!panel) return
  
      try {
        const message = panel.DeleteJob(projectSlug, path, subfolder)
        const count = JSON.parse(message.data)
        return count
      } catch (error) {
        // Do nothing. It is written at the end
        return undefined
      }
    }

  return {
    getFileBase64,
    saveFileBase64,
    fromFileToBase64,
    getFilesFromDir,
    fetchLocalRun,
    fetchLocalJob,
    fetchLocalLog,
    fileExplorer,
    getPaginatedJob,
    getJob,
    deleteJob,
    getPlatforms,
    simulationPath,
    cpuCount
  }
}